/**
 * שימוש — totals, fallback rate, daily table, top clinics, per-type split,
 * number health (tier / quality). Error codes: none (read-only, RTK logs CK-7408).
 */
import React, { useState } from "react";
import { WhatsappLogo, ChatText, Warning, ArrowCounterClockwise, Gauge, Info } from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import HoverTip from "../../../../components/ui/HoverTip";
import { useGetMessagingUsageQuery } from "../../../../store/services/nivApi";
import { MESSAGE_TYPES, formatWhen } from "./messagesShared";

const DAYS = [
  [7, "7 ימים"],
  [30, "30 יום"],
  [90, "90 יום"],
];

function Kpi({ icon: Icon, value, label, tone = "", info }) {
  return (
    <div className={`nmsg-kpi ${tone ? `nmsg-kpi-${tone}` : ""}`}>
      <span className="nmsg-kpi-icon">
        <Icon size={16} weight="fill" />
      </span>
      <div className="nmsg-kpi-info">
        <span className="nmsg-kpi-value">{value}</span>
        <span className="nmsg-kpi-label">
          {label}
          {info && (
            <HoverTip text={info}>
              <Info size={12} className="nmsg-kpi-info-icon" />
            </HoverTip>
          )}
        </span>
      </div>
    </div>
  );
}

export default function UsagePanel({ isAuthorized }) {
  const [days, setDays] = useState(30);
  const { data, isLoading, isFetching, refetch } = useGetMessagingUsageQuery({ days }, { skip: !isAuthorized });
  const t = data?.totals || {};
  const health = data?.health;

  return (
    <div className="nmsg-panel">
      <div className="nmsg-toolbar">
        <div className="nmsg-filters" role="group" aria-label="טווח">
          {DAYS.map(([d, l]) => (
            <button key={d} type="button" className={`nmsg-filter ${days === d ? "active" : ""}`} onClick={() => setDays(d)}>
              {l}
            </button>
          ))}
        </div>
        <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" onClick={() => refetch()} disabled={isFetching}>
          <ArrowCounterClockwise size={14} className={isFetching ? "spinning" : ""} /> רענון
        </button>
      </div>

      {isLoading ? (
        <ContentLoader size="sm" compact message="טוען…" />
      ) : (
        <>
          <div className="nmsg-kpis">
            <Kpi icon={WhatsappLogo} value={t.whatsappSent ?? 0} label="נשלחו בוואטסאפ" tone="whatsapp" />
            <Kpi icon={ChatText} value={t.smsFallback ?? 0} label="גיבוי SMS" tone="fallback" info="וואטסאפ לא עבד ו-SMS הצליח (CK-9265/9266)" />
            <Kpi icon={ChatText} value={t.smsDirect ?? 0} label="SMS ישיר" tone="sms" info="סוגים שמוגדרים SMS-ראשון (OTP, התראות ניב) או כשוואטסאפ כבוי" />
            <Kpi icon={Warning} value={(t.failedBoth ?? 0) + (t.failedProvider ?? 0)} label="לא נשלחו" tone="failed" info="נכשל בשני הערוצים / ללא ערוץ זמין (CK-9267)" />
            <Kpi icon={Gauge} value={`${data?.fallbackRate ?? 0}%`} label="שיעור גיבוי" tone={(data?.fallbackRate ?? 0) > 5 ? "fallback" : ""} info="יעד: פחות מ-5% מהשליחות" />
          </div>

          <div className="nmsg-two">
            <section className="nmsg-card">
              <header className="nmsg-card-head">
                <div className="nmsg-card-titles">
                  <span className="nmsg-eyebrow">24 שעות אחרונות</span>
                  <h4 className="nmsg-card-title">עכשיו</h4>
                </div>
              </header>
              <div className="nmsg-kv">
                <span>שליחות</span>
                <strong>{data?.last24h?.attempted ?? 0}</strong>
                <span>גיבוי SMS</span>
                <strong className={data?.last24h?.fallbacks > 20 ? "nmsg-bad" : ""}>{data?.last24h?.fallbacks ?? 0} ({data?.last24h?.fallbackRate ?? 0}%)</strong>
                <span>לא נשלחו</span>
                <strong className={data?.last24h?.failed > 0 ? "nmsg-bad" : ""}>{data?.last24h?.failed ?? 0}</strong>
                <span>נמענים ייחודיים היום (וואטסאפ)</span>
                <strong>{data?.uniqueRecipientsToday ?? 0}</strong>
              </div>
            </section>
            <section className="nmsg-card">
              <header className="nmsg-card-head">
                <div className="nmsg-card-titles">
                  <span className="nmsg-eyebrow">+972-54-505-0610</span>
                  <h4 className="nmsg-card-title">בריאות המספר</h4>
                </div>
              </header>
              {health ? (
                <div className="nmsg-kv">
                  <span>דרגת שליחה (Meta)</span>
                  <strong dir="ltr">{health.messagingLimit || "—"}</strong>
                  <span>אירוע איכות אחרון</span>
                  <strong dir="ltr">{health.lastQualityEvent || "—"} {health.lastQualityEventAt ? `· ${formatWhen(health.lastQualityEventAt)}` : ""}</strong>
                  <span>אירוע חשבון אחרון</span>
                  <strong dir="ltr">{health.lastAccountEvent || "—"}</strong>
                </div>
              ) : (
                <p className="nmsg-muted nmsg-help">
                  <Info size={13} /> עדיין לא התקבלו אירועי איכות מ-Meta. יש להירשם לשדות phone_number_quality_update / account_update ב-Meta App.
                </p>
              )}
              {data?.badTemplates?.length > 0 && (
                <p className="nmsg-bad nmsg-help">
                  <Warning size={13} weight="fill" /> תבניות בעייתיות: {data.badTemplates.map((b) => `${b.name} (${b.status})`).join(", ")}
                </p>
              )}
            </section>
          </div>

          <section className="nmsg-card">
            <header className="nmsg-card-head">
              <div className="nmsg-card-titles">
                <span className="nmsg-eyebrow">{data?.days} ימים</span>
                <h4 className="nmsg-card-title">לפי יום</h4>
              </div>
            </header>
            {data?.series?.length ? (
              <div className="nmsg-table nmsg-table-days" role="table">
                <div className="nmsg-tr nmsg-th" role="row">
                  <span>יום</span>
                  <span>וואטסאפ</span>
                  <span>גיבוי SMS</span>
                  <span>SMS ישיר</span>
                  <span>לא נשלחו</span>
                </div>
                {[...data.series].reverse().map((d) => (
                  <div key={d.day} className="nmsg-tr" role="row">
                    <span dir="ltr">{d.day}</span>
                    <span>{d.whatsappSent}</span>
                    <span className={d.smsFallback ? "nmsg-fallback" : ""}>{d.smsFallback}</span>
                    <span>{d.smsDirect}</span>
                    <span className={d.failed ? "nmsg-bad" : ""}>{d.failed}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="nmsg-muted nmsg-help">אין שליחות דרך הראוטר בטווח הזה.</p>
            )}
          </section>

          <div className="nmsg-two">
            <section className="nmsg-card">
              <header className="nmsg-card-head">
                <div className="nmsg-card-titles">
                  <span className="nmsg-eyebrow">top 10</span>
                  <h4 className="nmsg-card-title">קליניקות לפי שליחות</h4>
                </div>
              </header>
              {data?.topClinics?.length ? (
                <div className="nmsg-kv">
                  {data.topClinics.map((c) => (
                    <React.Fragment key={c.clinicId}>
                      <span>{c.clinicName || c.clinicId}</span>
                      <strong>
                        {c.total} {c.fallback ? <small className="nmsg-fallback">· {c.fallback} גיבוי</small> : null}
                        {c.failed ? <small className="nmsg-bad">· {c.failed} כשלים</small> : null}
                      </strong>
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <p className="nmsg-muted nmsg-help">אין נתונים.</p>
              )}
            </section>
            <section className="nmsg-card">
              <header className="nmsg-card-head">
                <div className="nmsg-card-titles">
                  <span className="nmsg-eyebrow">לפי סוג</span>
                  <h4 className="nmsg-card-title">פיצול ערוצים</h4>
                </div>
              </header>
              {data?.types?.length ? (
                <div className="nmsg-kv">
                  {data.types.map((r) => (
                    <React.Fragment key={r.messageType || "—"}>
                      <span>{MESSAGE_TYPES[r.messageType]?.label || r.messageType || "—"}</span>
                      <strong>
                        {r.whatsapp} WA · {r.smsFallback} גיבוי · {r.smsDirect} SMS{r.failed ? <small className="nmsg-bad"> · {r.failed} כשלים</small> : null}
                      </strong>
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <p className="nmsg-muted nmsg-help">אין נתונים.</p>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
